import Constants from '@/Constants';
import { Profile, profileType } from '@/models/Profile';
import { getDisplayName } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { listAchievementsRequest } from '@/store/achievements/actions';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import AchievementModalNotification from '../AchievementModalNotification';
import Brand from '../Brand';
import PersonImage from '../PersonImage';
import HeaderType from './components/headerType';
import Notification from './components/Notification';
import Search from './components/Search';
import MenuModal from './Modal';
import './style.scss';

export interface MenuTypeData {
    key: string;
    value: string;
    style: string;
    show?: boolean;
}

export interface MenuTypeData {
    key: string;
    value: string;
    style: string;
    exact: boolean;
}

const Header: React.FC = () => {
    const [actionType, setActionType] = useState<string>();
    const [showProfileChange, setShowProfileChange] = useState<boolean>(false);
    const [showAchievementsInfoModal, setShowAchievementsInfoModal] = useState<boolean>(false);
    const [logo, setLogo] = useState<string>();
    const dispatch = useDispatch();

    const { user, person, strings, organization, application, showMenu, achievements, hasSaml, isWhiteLabel, isTotvs } = useSelector(
        (state: ApplicationState) => ({
            strings: state.locale.data,
            user: state.identity.data.user,
            person: state.identity.data.person,
            organization: state.appOrganization.data.organization,
            application: state.appOrganization.data.application,
            showMenu: state.header.showMenu,
            achievements: state.achievements.personBadges,
            isWhiteLabel: state.appOrganization.data.isWhiteLabel,
            isTotvs: state.appOrganization.data.isTotvs,
            hasSaml: state.appOrganization.data.dynamicAttributes.saml,
        }),
    );

    const initialSelectedProfile = useCallback(() => {
        if (person.dynamicAttributes && person.dynamicAttributes.selectedProfile) {
            return person.dynamicAttributes.selectedProfile;
        }
        if (user.profiles) {
            return user.profiles[0].profileKey;
        }
        return Constants.STUDENT_PROFILE as profileType;
    }, []);

    const [selectedProfile, setSelectedProfile] = useState<profileType>(initialSelectedProfile);

    const [menuArray, setMenuArray] = useState<MenuTypeData[]>([]);

    useEffect(() => {
        setShowProfileChange(
            user.profiles.filter((p: Profile) => p.profileKey === Constants.HR_PROFILE || p.profileKey === Constants.STUDENT_PROFILE).length === 2,
        );
    }, [user.profiles]);

    useEffect(() => {
        if (!person.termsAccepted) {
            setActionType(Constants.headerMenuOptions.TERMS_OF_USE);
        } else if (checkIfMustChangePassword()) {
            setActionType(Constants.headerMenuOptions.PASSWORD_FIRST_TIME);
        } else if ((isWhiteLabel || isTotvs) && !person.onboarded) {
            setActionType(Constants.headerMenuOptions.ONBOARDING_WHITELABEL);
        } else {
            setActionType('');
        }
    }, []);

    const checkIfMustChangePassword = (): boolean => {
        return user.mustChangePassword && !hasSaml;
    };

    useEffect(() => {
        if (person.dynamicAttributes && person.dynamicAttributes.selectedProfile) {
            setSelectedProfile(person.dynamicAttributes.selectedProfile);
        }
    }, [person.dynamicAttributes]);

    useEffect(() => {
        if (organization && organization.orgKey && application && application.key) {
            setLogo(`${Constants.ORG_LOGO.replace('%s', organization.orgKey).replace('%s', application.key.toLowerCase())}?force=${Date.now()}`);
        }
    }, [organization, application]);

    useEffect(() => {
        if (actionType === '' && person.locale === Constants.language.PT && !person.achievementsInfoRead && achievements && achievements.length > 0) {
            setShowAchievementsInfoModal(true);
        }
    }, [person.achievementsInfoRead, actionType, achievements]);

    const handleModalClose = () => {
        switch (actionType) {
            case Constants.headerMenuOptions.TERMS_OF_USE:
                if (checkIfMustChangePassword()) {
                    setActionType(Constants.headerMenuOptions.PASSWORD_FIRST_TIME);
                } else if ((isWhiteLabel || isTotvs) && !person.onboarded) {
                    setActionType(Constants.headerMenuOptions.ONBOARDING_WHITELABEL);
                } else {
                    setActionType('');
                }
                break;
            case Constants.headerMenuOptions.PASSWORD:
            case Constants.headerMenuOptions.PASSWORD_FIRST_TIME:
                if ((isWhiteLabel || isTotvs) && !person.onboarded) {
                    setActionType(Constants.headerMenuOptions.ONBOARDING_WHITELABEL);
                } else {
                    setActionType('');
                }
                break;
            default:
                setActionType('');
        }
    };

    useEffect(() => {
        const HRMenuOptionsArray: Array<MenuTypeData> = [
            { key: strings.HOME, value: '/', style: 'nav-link', exact: true },
            { key: strings.CONTENTS, value: '/contents', style: 'nav-link', exact: false },
            { key: strings.USERS, value: '/people', style: 'nav-link', exact: true },
            { key: strings.DATA_PAGE.DATA, value: '/data', style: 'nav-link', exact: false },
            { key: strings.CONFIGURATIONS, value: '/config', style: 'nav-link', exact: false },
        ];

        const STMenuOptionsArray: Array<MenuTypeData> = [
            { key: strings.HOME, value: '/', style: 'nav-link', exact: true, show: true },
            { key: strings.MINE, value: `/mine/`, style: 'nav-link', exact: false, show: true },
            {
                key: strings.TRAINING_PAGE.MENU,
                value: `/trainings/`,
                style: 'nav-link',
                exact: false,
                show: true,
            },
            {
                key: strings.ACHIEVEMENTS.ACHIEVEMENTS_TITLE,
                value: `/achievements/`,
                style: 'nav-link',
                exact: false,
                show: strings._LANGUAGE === Constants.language.PT && achievements && achievements.length > 0,
            },
        ];

        switch (selectedProfile) {
            case Constants.STUDENT_PROFILE:
                setMenuArray(STMenuOptionsArray.filter((menu) => menu.show));
                break;
            case Constants.HR_PROFILE:
                setMenuArray(HRMenuOptionsArray);
                break;
            default:
                break;
        }
    }, [selectedProfile, strings._LANGUAGE, achievements]);

    useEffect(() => {
        if (person.id && strings._LANGUAGE === Constants.language.PT) {
            dispatch(listAchievementsRequest(person.id));
        }
    }, [strings._LANGUAGE]);

    const showProfile = () =>
        strings._LANGUAGE === Constants.language.PT &&
        !(person.dynamicAttributes && person.dynamicAttributes.selectedProfile && person.dynamicAttributes.selectedProfile === Constants.HR_PROFILE);

    return (
        <>
            {showAchievementsInfoModal && (
                <AchievementModalNotification onHide={() => setShowAchievementsInfoModal(false)} visible={showAchievementsInfoModal} />
            )}
            {actionType && <MenuModal content={actionType} handleModalClose={handleModalClose} />}
            <header className="header">
                <div className="container header-container">
                    <Link className="logo" style={{ backgroundImage: `url(${logo})` }} target="_self" to="/" />
                    {showMenu && (
                        <>
                            {menuArray.map((item) => (
                                <HeaderType key={item.key} menuType={item} />
                            ))}
                            {selectedProfile === Constants.STUDENT_PROFILE && <Search />}
                            {selectedProfile === Constants.STUDENT_PROFILE && <Notification />}
                            <div className="dropdown-container">
                                <div className="dropdown">
                                    <button className="btn btn-link btn-avatar" data-toggle="dropdown" type="button">
                                        <i className="material-icons va-m down">{Constants.icons.ARROW_DROP_DOWN}</i>
                                        <i className="material-icons va-m up">{Constants.icons.ARROW_DROP_UP}</i>
                                        <PersonImage size={32} src={person.thumbnail} />
                                    </button>

                                    <div className="dropdown-menu dropdown-menu-right">
                                        <div className="triangle-container">
                                            <div className="dropdown-triangle" />
                                            <div className="dropdown-triangle-border" />
                                        </div>

                                        <div className="dropdown-content">
                                            <div className="top-content">
                                                <PersonImage src={person.thumbnail} />
                                                <Link className="info-user" to="/profile/learningmap">
                                                    <span className="name">{getDisplayName(person)}</span>
                                                    <span className="mail">{person.email}</span>
                                                    {showProfile() && <span className="profile">{strings.VIEW_MY_PROFILE}</span>}
                                                </Link>
                                            </div>
                                            {showProfileChange && (
                                                <div
                                                    className="menu-item"
                                                    data-target="#change-modal"
                                                    data-toggle="modal"
                                                    onClick={() => setActionType(Constants.headerMenuOptions.PROFILE)}
                                                    onKeyDown={() => {}}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <span className="title">{strings.CHANGE_PROFILE}</span>
                                                    <i className="material-icons-outlined">{Constants.icons.ARROW_RIGHT}</i>
                                                </div>
                                            )}
                                            {!hasSaml && (
                                                <div
                                                    className="menu-item"
                                                    data-target="#change-modal"
                                                    data-toggle="modal"
                                                    id="password-change-modal"
                                                    onClick={() => setActionType(Constants.headerMenuOptions.PASSWORD)}
                                                    onKeyDown={() => {}}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <span className="title">{strings.CHANGE_PASSWORD}</span>
                                                    <i className="material-icons-outlined">{Constants.icons.ARROW_RIGHT}</i>
                                                </div>
                                            )}

                                            <div
                                                className="menu-item"
                                                data-target="#change-modal"
                                                data-toggle="modal"
                                                onClick={() => setActionType(Constants.headerMenuOptions.LANGUAGE)}
                                                onKeyDown={() => {}}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <span className="title">{strings.CHANGE_LANGUAGE}</span>
                                                <i className="material-icons-outlined">{Constants.icons.ARROW_RIGHT}</i>
                                            </div>
                                            <div
                                                className="menu-item"
                                                data-target="#change-modal"
                                                data-toggle="modal"
                                                onClick={() => setActionType(Constants.headerMenuOptions.FEEDBACK)}
                                                onKeyDown={() => {}}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <span className="title">{strings.SEND_FEEDBACK}</span>
                                                <i className="material-icons-outlined">{Constants.icons.ARROW_RIGHT}</i>
                                            </div>
                                            <NavLink className="nav-link text-center logout" to="/logout">
                                                {strings.LEAVE}
                                            </NavLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <Brand />
            </header>
        </>
    );
};

export default Header;
